import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Card, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography, } from "@mui/material";
import { Download as DownloadIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import DataTable from "react-data-table-component";
import { format } from "date-fns";
import { useTQuery } from "../../utils/useTQuery";

const TransactionReport = ({ userDetails }) => {
  const ID = userDetails?.ID;
  const UserType = userDetails?.USER_TYPE;
  const [selectedDateRange, setSelectedDateRange] = useState("All");
  const [selectedFacility, setSelectedFacility] = useState(UserType === "facility" ? ID : 'All');
  const [facilityCliniciansData, setFacilityCliniciansData] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [selectedClinician, setSelectedClinician] = useState("All");
  const [searchParams, setSearchParams] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    rowsPerPage: 10,
  });
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('desc');
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Set up debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchParams);
    }, 500); // Adjust the debounce delay (500ms)

    return () => clearTimeout(timer);
  }, [searchParams]);

  const { data: facilityClinicians, isLoading: isFacilityLoading } = useTQuery("facility-clinicians", `/report/facility-clinicians`, "get");
  const { data: reportData, isLoading: isReportLoading } = useTQuery(
    ["facility-job-payments", ID, pagination, selectedDateRange, selectedClinician, selectedFacility, debouncedSearch, sortField, sortDirection],
    `/report/facility-job-payments?${new URLSearchParams({
      FILTER_VALUE: debouncedSearch,
      SEARCH_PARMETER_LIST: searchParams.ParamsList,
      page: pagination.page,
      rowsPerPage: pagination.rowsPerPage,
      facilityID: UserType === "facility" ? ID : selectedFacility,
      clinicianID: selectedClinician || "",
      dateRange: selectedDateRange,
      FIELD: sortField,
      DIRECTION: sortDirection,
    }).toString()}`,
    "get"
  );

  // Separate query for CSV export - fetches all data without pagination
  const { data: allReportData, isLoading: isAllReportLoading } = useTQuery(
    ["facility-job-payments-all", ID, selectedDateRange, selectedClinician, selectedFacility, debouncedSearch, sortField, sortDirection],
    `/report/facility-job-payments?${new URLSearchParams({
      FILTER_VALUE: debouncedSearch,
      SEARCH_PARMETER_LIST: searchParams.ParamsList,
      page: 1,
      rowsPerPage: 100000, // Large number to get all data
      facilityID: UserType === "facility" ? ID : selectedFacility,
      clinicianID: selectedClinician || "",
      dateRange: selectedDateRange,
      FIELD: sortField,
      DIRECTION: sortDirection,
    }).toString()}`,
    "get"
  );
const isLoading = isFacilityLoading || isReportLoading;
  const allTransactionsData = useMemo(() => (allReportData?.STATUS) ? allReportData.OUTPUT : [], [allReportData]);
  useEffect(() => {
    if (facilityClinicians?.STATUS) {
      setFacilityCliniciansData(facilityClinicians.OUTPUT);

      if (UserType === 'facility') {
        const selectedFacility = facilityClinicians.OUTPUT.find(item => item.facilityId === ID);
        if (selectedFacility) {
          setClinicians(selectedFacility.clinicians);
          setSelectedClinician('All');
        }
      }
    } else {
      setFacilityCliniciansData([]);
    }
  }, [facilityClinicians]);

  const transactionsData = useMemo(() => (reportData?.STATUS) ? reportData.OUTPUT : [], [reportData]);

  const hasActiveFilters = useMemo(() => {
    return (
      selectedDateRange !== "All" ||
      selectedFacility !== (UserType === "facility" ? ID : 'All') ||
      selectedClinician !== "All" ||
      searchParams !== ""
    );
  }, [selectedDateRange, selectedFacility, selectedClinician, searchParams, UserType, ID]);

  const handleFacilityChange = (event) => {
    const facilityId = event.target.value;
    setSelectedFacility(facilityId);
    const selectedFacility = facilityCliniciansData.find(item => item.facilityId === facilityId);
    if (selectedFacility) {
      setClinicians(selectedFacility.clinicians);
      setSelectedClinician('All');
    }
  }
  const columns = [
    { name: "Facility Name", selector: (row) => row.FULL_NAME, sortable: true, wrap: true, minWidth: '150px' },
    { name: "Role Name", selector: (row) => row.ROLE_NAME, sortable: true, wrap: true, minWidth: '150px' },
    { name: "Specialty Name", selector: (row) => row.SPECIALTY_NAME, sortable: true, wrap: true, minWidth: '150px' },
    { name: "Job Type", selector: (row) => row.JOB_TYPE, sortable: true, wrap: true, minWidth: '150px' },
    { name: "Hiring Type", selector: (row) => row.HIRING_TYPE, sortable: true, wrap: true, minWidth: '150px' },
    { name: "Clinician Name", selector: (row) => row.clinicianName, sortable: true, wrap: true, minWidth: '150px' },
    { name: "Total Time", selector: (row) => row.TOTAL_TIME, sortable: true, wrap: true, minWidth: '150px' },
    { name: "Payment Amount", selector: (row) => row.paymentAmount, sortable: true, wrap: true, minWidth: '150px' },
    { name: "Payment Status", selector: (row) => row.transactionStatus, sortable: true, wrap: true, minWidth: '150px' },
    { name: "Payment Date", selector: (row) => row.transactionDateTime ? new Date(row.transactionDateTime) : new Date(0), sortable: true, wrap: true, minWidth: '100px', cell: (row) => row.transactionDateTime ? new Date(row.transactionDateTime).toDateString() : 'N/A' },
    { name: "Work Type", selector: (row) => row.workType, sortable: true, wrap: true, minWidth: '150px' },
  ];
  const handleDownloadCSV = useCallback(() => {
    const csvHeaders = [
      "Facility Name,Role Name,Specialty Name,Job Type,Hiring Type,Clinician Name,Total Time,Payment Amount,Payment Status,Payment Date,Work Type"
    ];

    const csvRows = allTransactionsData.map(row => (
      `"${row.FULL_NAME}","${row.ROLE_NAME}","${row.SPECIALTY_NAME}","${row.JOB_TYPE}","${row.HIRING_TYPE}","${row.clinicianName}","${row.TOTAL_TIME}","${row.paymentAmount}","${row.transactionStatus}","${row.transactionDateTime ? format(new Date(row.transactionDateTime), 'MMM dd, yyyy hh:mm a') : 'N/A'}","${row.workType}"`
    ));

    const csvContent = [csvHeaders, ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "transaction_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [allTransactionsData]);

  const handleResetFilters = useCallback(() => {
    setSelectedDateRange("All");
    setSelectedFacility(UserType === "facility" ? ID : 'All');
    setSelectedClinician("All");
    setSearchParams("");
    setSortField('');
    setSortDirection('desc');
    setPagination({
      page: 1,
      rowsPerPage: 10,
    });
    
    // Reset clinicians list if facility was changed
    if (UserType !== "facility") {
      const defaultFacility = facilityCliniciansData.find(item => item.facilityId === 'All');
      if (defaultFacility) {
        setClinicians(defaultFacility.clinicians || []);
      } else {
        setClinicians([]);
      }
    }
  }, [UserType, ID, facilityCliniciansData]);

  const handleSort = (column, sortDirection) => {
    // Map frontend column names to database field paths for sorting
    const fieldMapping = {
      'Facility Name': 'jobService.job.user.FULL_NAME',
      'Role Name': 'jobService.job.jobs_role.ROLE_NAME',
      'Specialty Name': 'jobService.job.specialty.SPECIALTY_NAME',
      'Job Type': 'jobService.job.JOB_TYPE',
      'Hiring Type': 'jobService.job.HIRING_TYPE',
      'Clinician Name': 'user.FULL_NAME',
      'Total Time': 'TOTAL_TIME',
      'Payment Amount': 'paymentAmount',
      'Payment Status': 'paymentTransaction.transactionStatus',
      'Payment Date': 'paymentTransaction.TRANSACTION_DATETIME',
      'Work Type': 'workType'
    };

    const fieldName = fieldMapping[column.name] || column.name;
    setSortField(fieldName);
    setSortDirection(sortDirection);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when sorting
  };


  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontSize: "18px", fontWeight: 700 }}>
              Transaction List
            </Typography>

            <Box className="table-filter-row" sx={{ display: "flex", justifyContent: "end", flexWrap: "wrap" , gap: 2, mb: 2 }}>
              <Grid item xs={12} md={2} >
                <FormControl fullWidth>
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    label="Date Range"
                    value={selectedDateRange}
                    onChange={(e) => setSelectedDateRange(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="thisWeek">This Week</MenuItem>
                    <MenuItem value="lastWeek">Last Week</MenuItem>
                    <MenuItem value="currentMonth">Current Month</MenuItem>
                    <MenuItem value="lastMonth">Last Month</MenuItem>

                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2} >
                <FormControl fullWidth>
                  <InputLabel>Filter by Facility</InputLabel>
                  <Select
                    label="Filter by Clinician"
                    value={selectedFacility}
                    disabled={UserType === "facility"}
                    onChange={handleFacilityChange}
                  >
                    <MenuItem value="All">All</MenuItem>
                    {facilityCliniciansData.map((facility) => (
                      <MenuItem key={facility.facilityId} value={facility.facilityId}>
                        {facility.facilityName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2} >
                <FormControl fullWidth>
                  <InputLabel>Filter by Clinician</InputLabel>
                  <Select
                    label="Filter by Clinician"
                    value={selectedClinician}
                    onChange={(e) => setSelectedClinician(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    {clinicians.map((clinician) => (
                      <MenuItem key={clinician.clinicianId} value={clinician.clinicianId}>
                        {clinician.clinicianName + "( " + clinician.clinicianRole + " )"}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <TextField
                label="Search Transactions"
                placeholder="Search by facility, clinician, speciality, amount, status, job type..."
                variant="outlined"
                size="small"
                value={searchParams}
                onChange={(e) => setSearchParams(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-input': {
                    padding: 2,
                  },
                }}
              />

              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleResetFilters}
                sx={{
                  padding: "16px 20px",
                  borderColor: "#71ba32",
                  color: "#71ba32",
                  '&:hover': {
                    borderColor: "#4eb3e8",
                    backgroundColor: "rgba(78, 179, 232, 0.04)",
                  },
                }}
              >
                Reset Filters
              </Button>

              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadCSV}
                disabled={isAllReportLoading}
                sx={{
                  padding: "16px 20px",
                  background: "linear-gradient(-130deg, #71ba32, #4eb3e8) !important",
                  color: "#ffffff",
                }}
              >
                {isAllReportLoading ? "Preparing CSV..." : "Download CSV"}
              </Button>
            </Box>

            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
                <CircularProgress />
              </Box>
            ) : (
              <DataTable
                columns={columns}
                data={transactionsData}
                sortServer
                onSort={handleSort}
                pagination
                paginationServer
                paginationTotalRows={reportData?.meta?.totalItems || 0}
                paginationPerPage={pagination.rowsPerPage}
                paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
                paginationDefaultPage={pagination.page}
                onChangePage={(page) => setPagination((prev) => ({ ...prev, page }))}
                onChangeRowsPerPage={(rowsPerPage) => setPagination((prev) => ({ ...prev, rowsPerPage, page: 1 }))}
              />
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TransactionReport;